/**
 * V104-G-10: pure helpers for the rule-authoring form.
 *
 * - A plain-label catalog over the frozen ADR-0024 vocabulary (the field keys
 *   never reach the screen).
 * - A simple condition model (all / any of field conditions) and its
 *   conversion to and from the predicate AST.
 * - The quote check, using the same normalization the server's grounding
 *   check uses (`normalizeForGrounding`, ADR-0076 §6), plus a locator that
 *   maps a match back to the raw text for highlighting.
 */
import {
  AFFECTED_PERSONS,
  AFFECTED_PERSON_LABELS,
  AI_ACTIVITIES,
  AI_ACTIVITY_LABELS,
  ARRAY_FIELDS,
  BOOLEAN_FIELDS,
  DATA_CLASSES,
  DATA_CLASS_LABELS,
  MIN_EXCERPT_CHARS,
  SCALAR_FIELDS,
  evaluatePredicate,
  generateRuleFixtures,
  normalizeForGrounding,
  CANONICAL_ROLES,
  CANONICAL_ROLE_LABEL,
  CANONICAL_TIER_LABEL,
  RULE_TIERS,
  canonicalScope,
  termForCanonical,
} from '@aigovops/core';
import type { CanonicalRole, DeadlineRule, JurisdictionTerm, Predicate, RuleTier } from '@aigovops/core';

// ---- Vocabulary catalog --------------------------------------------------------

export type FieldKind = 'boolean' | 'scalar' | 'array';
export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  /** Known values (value → label); free text when absent. */
  options?: Array<{ value: string; label: string }>;
}

const BOOLEAN_LABELS: Record<string, string> = {
  employment_decision: 'Makes employment decisions',
  consequential_decision: 'Makes consequential decisions',
  credit_decision: 'Makes credit decisions',
  healthcare_decision: 'Makes healthcare decisions',
  law_enforcement: 'Used in law enforcement',
  critical_infrastructure: 'Runs critical infrastructure',
  biometric_identification: 'Identifies people by biometrics',
  social_scoring: 'Scores people socially',
  subliminal_manipulation: 'Uses subliminal manipulation',
  exploits_vulnerabilities: 'Exploits vulnerable people',
  public_authority: 'Used by a public authority',
  federal_flowdown: 'Federal contract passes OMB AI terms down',
};
const SCALAR_LABELS: Record<string, string> = {
  gpai_role: 'General-purpose model role',
  autonomy_level: 'How autonomous it is',
  taxonomy_type: 'Kind of AI system',
  decision_role: 'Its part in decisions',
  human_oversight_mode: 'Human oversight',
  sector: 'Sector',
  deployment_target: 'Where it runs',
  deployer_type: 'US federal role',
};
const ARRAY_LABELS: Record<string, string> = {
  affected_persons: 'Who it affects',
  data_classes: 'Data it uses',
  deployment_jurisdictions: 'Where it is deployed',
  data_categories: 'Data categories',
  affected_parties: 'Affected parties',
  embedded_third_party_ai: 'Third-party AI inside it',
  output_destinations: 'Where its output goes',
  affected_person_locations: 'Where affected people are',
  ai_activity: 'What the AI does',
};

const opts = (values: readonly string[], labels: Record<string, string>) => values.map((v) => ({ value: v, label: labels[v] ?? v.replace(/_/g, ' ') }));
const SCALAR_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  gpai_role: [
    { value: 'none', label: 'None' },
    { value: 'provider', label: 'Provider' },
    { value: 'deployer', label: 'Deployer' },
  ],
  autonomy_level: opts(['fully_automated', 'human_in_the_loop', 'human_on_the_loop', 'decision_support', 'informational'], {
    fully_automated: 'Fully automated',
    human_in_the_loop: 'Human in the loop',
    human_on_the_loop: 'Human on the loop',
    decision_support: 'Decision support',
    informational: 'Informational',
  }),
  deployer_type: [
    { value: 'federal_agency', label: 'Federal agency' },
    { value: 'government_contractor', label: 'Federal contractor or vendor' },
    { value: 'other', label: 'Neither' },
  ],
};
const ARRAY_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  ai_activity: opts(AI_ACTIVITIES, AI_ACTIVITY_LABELS),
  affected_persons: opts(AFFECTED_PERSONS, AFFECTED_PERSON_LABELS),
  data_classes: opts(DATA_CLASSES, DATA_CLASS_LABELS),
};

export const FIELD_CATALOG: FieldSpec[] = [
  ...ARRAY_FIELDS.map((k) => ({ key: k, label: ARRAY_LABELS[k] ?? k, kind: 'array' as const, options: ARRAY_OPTIONS[k] })),
  ...BOOLEAN_FIELDS.map((k) => ({ key: k, label: BOOLEAN_LABELS[k] ?? k, kind: 'boolean' as const })),
  ...SCALAR_FIELDS.map((k) => ({ key: k, label: SCALAR_LABELS[k] ?? k, kind: 'scalar' as const, options: SCALAR_OPTIONS[k] })),
].sort((a, b) => a.label.localeCompare(b.label));

export function fieldSpec(key: string): FieldSpec | undefined {
  return FIELD_CATALOG.find((f) => f.key === key);
}

// ---- Conditions ↔ predicate ----------------------------------------------------

export type Operator = 'is' | 'is_not' | 'includes' | 'has_any';
export interface Condition {
  field: string;
  op: Operator;
  /** 'true' / 'false' for yes/no fields; the value otherwise; unused for has_any. */
  value: string;
}
export type Combinator = 'all' | 'any';

export const OPERATOR_LABEL: Record<Operator, string> = {
  is: 'is',
  is_not: 'is not',
  includes: 'includes',
  has_any: 'has any value',
};

export function operatorsFor(kind: FieldKind): Operator[] {
  return kind === 'array' ? ['includes', 'has_any'] : kind === 'boolean' ? ['is'] : ['is', 'is_not'];
}

export function blankCondition(field = 'ai_activity'): Condition {
  const spec = fieldSpec(field);
  const kind = spec?.kind ?? 'array';
  return { field, op: operatorsFor(kind)[0]!, value: kind === 'boolean' ? 'true' : spec?.options?.[0]?.value ?? '' };
}

/** A condition is complete when it has a value (or doesn't need one). */
export function conditionComplete(c: Condition): boolean {
  return c.op === 'has_any' || c.value.trim().length > 0;
}

function leaf(c: Condition): Predicate {
  const spec = fieldSpec(c.field);
  if (spec?.kind === 'boolean') return { eq: { field: c.field, value: c.value !== 'false' } } as Predicate;
  if (c.op === 'has_any') return { non_empty: { field: c.field } } as Predicate;
  if (c.op === 'includes') return { contains: { field: c.field, value: c.value.trim() } } as Predicate;
  const eq = { eq: { field: c.field, value: c.value.trim() } } as Predicate;
  return c.op === 'is_not' ? ({ not: eq } as Predicate) : eq;
}

export function toPredicate(combinator: Combinator, conditions: Condition[]): Predicate | null {
  const leaves = conditions.filter(conditionComplete).map(leaf);
  if (leaves.length === 0) return null;
  if (leaves.length === 1) return leaves[0]!;
  return (combinator === 'all' ? { and: leaves } : { or: leaves }) as Predicate;
}

function fromLeaf(node: Record<string, unknown>): Condition | null {
  if ('not' in node) {
    const inner = node.not as Record<string, unknown>;
    const c = inner && 'eq' in inner ? fromLeaf(inner) : null;
    if (!c || fieldSpec(c.field)?.kind !== 'scalar') return null;
    return { ...c, op: 'is_not' };
  }
  if ('eq' in node) {
    const { field, value } = node.eq as { field: string; value: unknown };
    const spec = fieldSpec(field);
    if (!spec || spec.kind === 'array') return null;
    return { field, op: 'is', value: String(value) };
  }
  if ('contains' in node) {
    const { field, value } = node.contains as { field: string; value: unknown };
    return fieldSpec(field)?.kind === 'array' ? { field, op: 'includes', value: String(value) } : null;
  }
  if ('non_empty' in node) {
    const { field } = node.non_empty as { field: string };
    return fieldSpec(field)?.kind === 'array' ? { field, op: 'has_any', value: '' } : null;
  }
  return null;
}

/** The builder's view of a predicate, or null when it is more complex than all/any of conditions. */
export function fromPredicate(p: unknown): { combinator: Combinator; conditions: Condition[] } | null {
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  const node = p as Record<string, unknown>;
  for (const [key, combinator] of [['and', 'all'], ['or', 'any']] as const) {
    if (key in node) {
      const kids = node[key];
      if (!Array.isArray(kids) || kids.length === 0) return null;
      const conditions = kids.map((k) => (k && typeof k === 'object' ? fromLeaf(k as Record<string, unknown>) : null));
      return conditions.every(Boolean) ? { combinator, conditions: conditions as Condition[] } : null;
    }
  }
  const one = fromLeaf(node);
  return one ? { combinator: 'all', conditions: [one] } : null;
}

// ---- Duties ------------------------------------------------------------------------

export type DeadlineKind = DeadlineRule['kind'];
export const DEADLINE_LABEL: Record<DeadlineKind, string> = {
  none: 'No deadline',
  duration: 'Within … hours',
  before_event: '… hours before',
  recurring: 'Every … months',
  business_days: 'Within … business days',
};
/** ADR-0077 §6: neutral labels when the jurisdiction declares no tier words. */
export const TIER_LABEL: Record<RuleTier, string> = CANONICAL_TIER_LABEL;

// ---- Jurisdiction terms (V104-G-12 / ADR-0077 §6) ------------------------------

/** One "Only for" / "Risk tier" option: a jurisdiction term, or a canonical value with a neutral label. */
export interface LabelChoice {
  /** The term key (term mode) or the canonical value (plain mode). */
  value: string;
  label: string;
  /** The canonical value it counts as. */
  canonical: string;
  citation?: string;
  definition?: string | null;
  /** Filed but not yet approved: pickable, but the rule applies only once it is approved. */
  pending?: boolean;
}
export interface LabelChoices {
  /** 'terms' when the jurisdiction declares words of this kind; 'plain' otherwise. */
  mode: 'terms' | 'plain';
  choices: LabelChoice[];
}

function termChoice(t: JurisdictionTerm): LabelChoice {
  return { value: t.term_key, label: t.label, canonical: t.canonical, citation: t.citation, definition: t.definition, pending: t.status === 'pending' };
}

/** "Only for": the jurisdiction's role words, or the canonical roles with plain labels. */
export function roleChoices(terms: readonly JurisdictionTerm[]): LabelChoices {
  const own = terms.filter((t) => t.kind === 'role' && t.status !== 'deprecated');
  if (own.length) return { mode: 'terms', choices: own.map(termChoice) };
  return { mode: 'plain', choices: CANONICAL_ROLES.map((r) => ({ value: r, label: CANONICAL_ROLE_LABEL[r], canonical: r })) };
}

/** "Risk tier": the jurisdiction's tier words strictest first, or the canonical ladder with plain labels. */
export function tierChoices(terms: readonly JurisdictionTerm[]): LabelChoices {
  const own = terms
    .filter((t) => t.kind === 'tier' && t.status !== 'deprecated')
    .sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0));
  if (own.length) return { mode: 'terms', choices: own.map(termChoice) };
  return { mode: 'plain', choices: RULE_TIERS.map((t) => ({ value: t, label: CANONICAL_TIER_LABEL[t], canonical: t })) };
}

/**
 * The obligation's label fields for filing. Term mode writes the term keys AND
 * their canonical values (the editor never types a canonical value); plain
 * mode writes canonical values only.
 */
export function dutyLabelFields(
  d: Pick<DutyDraft, 'scope' | 'roleTerms' | 'tier' | 'tierTerm'>,
  terms: readonly JurisdictionTerm[]
): { scope?: CanonicalRole[]; role_terms?: string[]; tier?: RuleTier; tier_term?: string } {
  const out: { scope?: CanonicalRole[]; role_terms?: string[]; tier?: RuleTier; tier_term?: string } = {};
  const roles = roleChoices(terms);
  if (roles.mode === 'terms') {
    const picked = roles.choices.filter((c) => d.roleTerms.includes(c.value));
    if (picked.length) {
      out.role_terms = picked.map((c) => c.value);
      out.scope = [...new Set(picked.map((c) => c.canonical as CanonicalRole))];
    } else if (d.scope.length) {
      // A plain pick made before the law had a word for it: filed as the fixed value alone.
      out.scope = d.scope;
    }
  } else if (d.scope.length) {
    out.scope = d.scope;
  }
  const tiers = tierChoices(terms);
  if (tiers.mode === 'terms') {
    const t = tiers.choices.find((c) => c.value === d.tierTerm);
    if (t) {
      out.tier_term = t.value;
      out.tier = t.canonical as RuleTier;
    } else if (d.tier) {
      out.tier = d.tier;
    }
  } else if (d.tier) {
    out.tier = d.tier;
  }
  return out;
}

/**
 * A duty that came in with canonical values only (a pipeline draft, a saved
 * draft) picks up the jurisdiction's word when exactly one maps to it
 * (ADR-0077 §3). Term fields already set are kept.
 */
export function adoptTerms(d: DutyDraft, terms: readonly JurisdictionTerm[]): DutyDraft {
  const next = { ...d };
  if (!next.roleTerms.length && next.scope.length) {
    const keys = next.scope.map((r) => termForCanonical(terms as JurisdictionTerm[], 'role', r)?.term_key).filter((k): k is string => !!k);
    if (keys.length === next.scope.length) {
      next.roleTerms = keys;
      next.scope = [];
    }
  }
  if (!next.tierTerm && next.tier) {
    const key = termForCanonical(terms as JurisdictionTerm[], 'tier', next.tier)?.term_key;
    if (key) {
      next.tierTerm = key;
      next.tier = '';
    }
  }
  return next;
}

export interface DutyDraft {
  key: number;
  title: string;
  /** Plain words; stored as snake_case (see toDutyId). */
  duty: string;
  citation: string;
  deadline: DeadlineKind;
  amount: string;
  basis: string;
  /** Canonical tier, used when the jurisdiction declares no tier words. */
  tier: RuleTier | '';
  /** Canonical roles, used when the jurisdiction declares no role words. */
  scope: CanonicalRole[];
  /** V104-G-12: the jurisdiction's role words picked under "Only for" (term keys). */
  roleTerms: string[];
  /** V104-G-12: the jurisdiction's tier word picked under "Risk tier" (term key). */
  tierTerm: string;
  quote: string;
  /** V104-G-11: this duty's own start date (YYYY-MM-DD); empty = it starts with the rule. */
  startsOn: string;
  /** The short name follows the title until the editor edits it. */
  autoName: boolean;
  /** V112-5-5 (ADR-0116): set when the editor proposes the short name as a new duty. */
  newDuty?: NewDutyDraft;
}

/** V112-5-5: a new duty proposed from the form; filed with the rule, in one batch with its control. */
export interface NewDutyDraft {
  /** False after "Don’t propose": the typed fields are kept (round 1 #9), nothing is filed. */
  open: boolean;
  label: string;
  definition: string;
  controlMode: 'existing' | 'new';
  controlId: string;
  controlTitle: string;
  controlDescription: string;
}

/** Round 1 #10: with no controls to pick from, a new control is the only choice. */
export function blankNewDuty(title: string, hasControls = true): NewDutyDraft {
  return { open: true, label: title.trim(), definition: '', controlMode: hasControls ? 'existing' : 'new', controlId: '', controlTitle: '', controlDescription: '' };
}

/** True when the card proposes its short name as a new duty (open, not closed with "Don’t propose"). */
export function proposing(d: Pick<DutyDraft, 'newDuty'>): d is { newDuty: NewDutyDraft } {
  return !!d.newDuty && d.newDuty.open !== false;
}

function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      best = Math.min(best, cur[j]!);
    }
    if (best > max) return max + 1;
    prev = cur;
  }
  return prev[b.length]!;
}

/**
 * Round 1 #1: the known duty an unknown short name most likely means — a small typo (edit distance ≤ 3) or most of
 * the same words — so reusing a duty is the easy path and a new one the deliberate exception. Null when none is close.
 */
export function closestDuty(id: string, known: readonly string[]): string | null {
  if (id.length < 3) return null;
  // Words longer than 2 letters, and the numbers in either name ("48h" and "48 hours" share 48).
  const parts = (x: string) => {
    const tokens = x.split('_').filter(Boolean);
    return { words: new Set(tokens.filter((w) => w.length > 2 && !/\d/.test(w))), numbers: new Set(tokens.flatMap((w) => w.match(/\d+/g) ?? [])) };
  };
  const mine = parts(id);
  let best: { duty: string; score: number } | null = null;
  for (const k of known) {
    const d = editDistance(id, k, 3);
    let score = d <= 3 ? 10 - d : 0;
    if (!score) {
      const theirs = parts(k);
      const words = [...theirs.words].filter((w) => mine.words.has(w)).length;
      const numbers = [...theirs.numbers].filter((n) => mine.numbers.has(n)).length;
      const span = Math.max(mine.words.size, theirs.words.size) || 1;
      if (words >= 2 && words / span >= 0.5) score = 5 + words / span;
      // One shared word plus a shared number: "remove … within 48 hours" ↔ takedown_within_48h.
      else if (words >= 1 && numbers >= 1) score = 4 + words / span;
    }
    if (score && (!best || score > best.score)) best = { duty: k, score };
  }
  return best?.duty ?? null;
}

/**
 * Round 1 #7: a server refusal about a new duty, in the form's words and pointed at the field to fix. `cards` are the
 * duty cards in order (key + short-name id). Null when the issue is not about a duty's name.
 */
export function plainNewDutyIssue(issue: string, cards: ReadonlyArray<{ key: number; id: string }>): { target: string; text: string } | null {
  const unknown = /^obligations\[(\d+)\]\.duty: '([^']+)' is not a known duty/.exec(issue);
  if (unknown) {
    const i = Number(unknown[1]);
    const c = cards[i];
    if (c) return { target: `dnp-${c.key}`, text: `Duty ${i + 1}: “${unknown[2]!.replace(/_/g, ' ')}” isn’t a known duty. Propose it, or use a known duty.` };
  }
  const m = /^new duty '([^']+)': (.*)$/.exec(issue);
  if (!m) return null;
  const i = cards.findIndex((c) => c.id === m[1]);
  if (i < 0) return null;
  const k = cards[i]!.key;
  const rest = m[2]!;
  const n = `Duty ${i + 1}`;
  if (/is not a control/.test(rest)) return { target: `ndc-${k}`, text: `${n}: the control you picked no longer exists. Pick another.` };
  if (/already exists; pick it/.test(rest)) return { target: `ndm-existing-${k}`, text: `${n}: a control with this name already exists. Choose it under “A control we have”.` };
  if (/already a known duty/.test(rest)) return { target: `dn-${k}`, text: `${n}: this is now a known duty, so it no longer needs a proposal. File the rule again.` };
  if (/label is required/.test(rest)) return { target: `ndl-${k}`, text: `${n}: name the new duty (up to 120 characters).` };
  if (/definition is required/.test(rest)) return { target: `ndd-${k}`, text: `${n}: say what the new duty requires (up to 1,000 characters).` };
  if (/snake_case/.test(rest)) return { target: `dn-${k}`, text: `${n}: use letters, numbers and single spaces in the short name.` };
  if (/title and a description/.test(rest)) return { target: `ndt-${k}`, text: `${n}: give the new control a title and say what it checks.` };
  return { target: `dn-${k}`, text: `${n}: ${rest}` };
}

/** Problems with a proposed new duty, as plain sentences for the form's summary (empty = complete). */
export function newDutyProblems(nd: NewDutyDraft): Array<{ field: 'ndl' | 'ndd' | 'ndc' | 'ndt' | 'ndx'; text: string }> {
  const out: Array<{ field: 'ndl' | 'ndd' | 'ndc' | 'ndt' | 'ndx'; text: string }> = [];
  if (!nd.label.trim()) out.push({ field: 'ndl', text: 'name the new duty.' });
  if (!nd.definition.trim()) out.push({ field: 'ndd', text: 'say what the new duty requires.' });
  if (nd.controlMode === 'existing' && !nd.controlId) out.push({ field: 'ndc', text: 'choose the control that checks the new duty.' });
  if (nd.controlMode === 'new' && !nd.controlTitle.trim()) out.push({ field: 'ndt', text: 'give the new control a title.' });
  if (nd.controlMode === 'new' && !nd.controlDescription.trim()) out.push({ field: 'ndx', text: 'say what the new control checks.' });
  return out;
}

/** "Takedown within 48h" → takedown_within_48h. */
export function toDutyId(words: string): string {
  return words.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[^a-z]+/, '').slice(0, 64);
}

export function toDeadlineRule(d: Pick<DutyDraft, 'deadline' | 'amount' | 'basis'>): DeadlineRule | null {
  if (d.deadline === 'none') return { kind: 'none' };
  const n = Number(d.amount);
  if (!Number.isInteger(n) || n <= 0) return null;
  const basis = d.basis.trim() ? { basis: d.basis.trim() } : {};
  switch (d.deadline) {
    case 'duration':
      return { kind: 'duration', hours: n, ...basis };
    case 'before_event':
      return { kind: 'before_event', hours: n, ...basis };
    case 'recurring':
      return { kind: 'recurring', months: n, ...basis };
    case 'business_days':
      return { kind: 'business_days', days: n, ...basis };
  }
}

let dutyKey = 0;
export function blankDuty(citation = ''): DutyDraft {
  return { key: ++dutyKey, title: '', duty: '', citation, deadline: 'none', amount: '', basis: '', tier: '', scope: [], roleTerms: [], tierTerm: '', quote: '', startsOn: '', autoName: true };
}

/** A duty from the pipeline's partial draft (unknown shape; read defensively). */
export function dutyFromDraft(raw: unknown, fallbackCitation: string): DutyDraft {
  const o = (raw ?? {}) as Record<string, unknown>;
  const d = blankDuty(typeof o.citation === 'string' && o.citation ? o.citation : fallbackCitation);
  d.title = typeof o.title === 'string' ? o.title : '';
  d.duty = typeof o.duty === 'string' ? o.duty.replace(/_/g, ' ') : '';
  d.autoName = !d.duty;
  d.quote = typeof o.source_excerpt === 'string' ? o.source_excerpt : '';
  d.startsOn = typeof o.effective_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.effective_date) ? o.effective_date : '';
  const rule = o.deadline_rule as Record<string, unknown> | undefined;
  if (rule && typeof rule.kind === 'string' && rule.kind in DEADLINE_LABEL) {
    d.deadline = rule.kind as DeadlineKind;
    const n = rule.hours ?? rule.months ?? rule.days;
    d.amount = typeof n === 'number' ? String(n) : '';
    d.basis = typeof rule.basis === 'string' ? rule.basis : '';
  }
  if (typeof o.tier === 'string' && o.tier in TIER_LABEL) d.tier = o.tier as RuleTier;
  d.scope = canonicalScope(o.scope) ?? [];
  if (Array.isArray(o.role_terms)) d.roleTerms = o.role_terms.filter((k): k is string => typeof k === 'string');
  if (typeof o.tier_term === 'string') d.tierTerm = o.tier_term;
  return d;
}

// ---- Quotes ------------------------------------------------------------------------

export type QuoteStatus = 'empty' | 'too_short' | 'not_in_text' | 'found';

/** The server's grounding rule, client side: ≥ 40 normalized characters, in the normalized text. */
export function checkQuote(normalizedText: string, quote: string): QuoteStatus {
  if (!quote.trim()) return 'empty';
  const n = normalizeForGrounding(quote);
  if (n.length < MIN_EXCERPT_CHARS) return 'too_short';
  return normalizedText.includes(n) ? 'found' : 'not_in_text';
}

/** One plain message per status; "too short" carries a live count. */
export function quoteMessage(status: Exclude<QuoteStatus, 'empty'>, quote: string): string {
  if (status === 'found') return 'Found in the law text.';
  if (status === 'too_short') {
    return `Too short: include the words around it (${normalizeForGrounding(quote).length} of ${MIN_EXCERPT_CHARS} characters).`;
  }
  return 'Not in the law text. Copy it word for word, or select it in the law text and use it as the quote.';
}

/** Amount label that follows the deadline type. */
export const AMOUNT_LABEL: Record<Exclude<DeadlineKind, 'none'>, string> = {
  duration: 'Hours',
  before_event: 'Hours before',
  recurring: 'Months',
  business_days: 'Business days',
};

/**
 * Where a quote sits in the raw text: [start, end) in raw indices, or null.
 * Mirrors normalizeForGrounding per character (letters and digits kept,
 * whitespace collapsed, everything else dropped) and keeps an index map.
 */
export function locateQuote(raw: string, quote: string): { start: number; end: number } | null {
  const target = normalizeForGrounding(quote);
  if (!target) return null;
  let out = '';
  const map: number[] = [];
  let lastSpace = true;
  for (let i = 0; i < raw.length; i++) {
    const piece = raw[i]!.normalize('NFKC').toLowerCase();
    for (const ch of piece) {
      if (/\s/u.test(ch)) {
        if (!lastSpace) {
          out += ' ';
          map.push(i);
          lastSpace = true;
        }
      } else if (/[\p{L}\p{N}]/u.test(ch)) {
        out += ch;
        map.push(i);
        lastSpace = false;
      }
    }
  }
  const at = out.indexOf(target);
  if (at < 0) return null;
  return { start: map[at]!, end: map[at + target.length - 1]! + 1 };
}

// ---- Test cases (V104-G-13) ----------------------------------------------------------
//
// A case describes an AI system in plain facts (pick a rule field and a value)
// and says whether the rule should apply to it. The editor never sees the raw
// classification: facts convert to and from it here, and each case is checked
// with the same predicate evaluation the server uses (`validateTestFixtures`).

/** "Any other value": stands for a value the rule does not name. Stored as a value no rule can name. */
export const ELSE_VALUE = '__else__';
const ELSE_STORED = '__not_other';

export interface Fact {
  key: number;
  field: string;
  value: string;
}
export interface CaseDraft {
  key: number;
  /** Plain name; optional. */
  label: string;
  /** The rule should apply to this system (true) or not (false). */
  expected: boolean;
  facts: Fact[];
}
export type CaseState = 'agrees' | 'disagrees' | 'incomplete' | 'error';
export interface CaseResult {
  state: CaseState;
  /** What the rule says about this case; absent when incomplete or errored. */
  actual?: boolean;
}

/** Keys are positive and unique within the list; computed from what is there so a restored draft never collides. */
export const nextCaseKey = (cases: CaseDraft[]) => Math.max(0, ...cases.map((c) => c.key)) + 1;
export const nextFactKey = (cases: CaseDraft[]) => Math.max(0, ...cases.flatMap((c) => c.facts.map((f) => f.key))) + 1;
export function blankFact(key: number, field = 'ai_activity'): Fact {
  const c = blankCondition(field);
  return { key, field, value: c.op === 'has_any' ? '' : c.value };
}
export function blankCase(cases: CaseDraft[], expected: boolean): CaseDraft {
  return { key: nextCaseKey(cases), label: '', expected, facts: [blankFact(nextFactKey(cases))] };
}

function factIsComplete(f: Fact): boolean {
  return f.value.trim().length > 0 || fieldSpec(f.field)?.kind === 'boolean';
}

/** The classification a case describes; incomplete facts are left out. */
export function caseClassification(c: CaseDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of c.facts) {
    if (!factIsComplete(f)) continue;
    const kind = fieldSpec(f.field)?.kind;
    const value = f.value === ELSE_VALUE ? ELSE_STORED : f.value.trim();
    if (kind === 'boolean') out[f.field] = f.value !== 'false';
    else if (kind === 'array') out[f.field] = [...((out[f.field] as string[] | undefined) ?? []), value];
    else out[f.field] = value;
  }
  return out;
}

/** Cases from stored or generated fixtures. Keys are fixed (negative for generated ones), so ids survive a re-render. */
export function casesFromFixtures(
  fixtures: Array<{ label?: string; classification: Record<string, unknown>; expected: boolean }>
): CaseDraft[] {
  return fixtures.map((fx, i) => {
    const facts: Fact[] = [];
    for (const [field, raw] of Object.entries(fx.classification ?? {})) {
      const kind = fieldSpec(field)?.kind;
      if (!kind) continue;
      const values = Array.isArray(raw) ? raw : [raw];
      for (const v of values) {
        const str = typeof v === 'string' && v.startsWith('__not_') ? ELSE_VALUE : String(v);
        facts.push({ key: -(i * 100 + facts.length + 1), field, value: kind === 'boolean' ? String(v !== false) : str });
      }
    }
    return { key: -(i + 1), label: fx.label ?? '', expected: fx.expected, facts };
  });
}

/** The starting set: the same two cases the server would build from the conditions (stable keys). */
export function startingCases(predicate: Predicate | null): CaseDraft[] {
  if (!predicate) return [];
  const made = generateRuleFixtures({ predicate } as Parameters<typeof generateRuleFixtures>[0]);
  return casesFromFixtures(
    made.map((f) => ({
      label: f.expected ? 'A system the rule covers' : 'A system the rule does not cover',
      classification: f.classification as Record<string, unknown>,
      expected: f.expected,
    }))
  );
}

export function checkCase(predicate: Predicate | null, c: CaseDraft): CaseResult {
  if (!predicate || c.facts.some((f) => !factIsComplete(f))) return { state: 'incomplete' };
  try {
    const actual = evaluatePredicate(predicate, caseClassification(c) as never).matched;
    return { state: actual === c.expected ? 'agrees' : 'disagrees', actual };
  } catch {
    return { state: 'error' };
  }
}

/** Plain sentence for one case result. */
export function caseMessage(r: CaseResult, c: CaseDraft): string {
  if (r.state === 'agrees') return c.expected ? 'The rule applies to this system, as expected.' : 'The rule does not apply to this system, as expected.';
  if (r.state === 'incomplete') return (c.facts.some((f) => !factIsComplete(f)) ? 'Choose a value for each fact to check this case.' : 'Add a condition above to check this case.');
  if (r.state === 'error') return 'This case can’t be checked against the conditions.';
  return r.actual
    ? 'Your conditions apply to this system, but you expect them not to. Change the case or the conditions.'
    : 'Your conditions don’t apply to this system, but you expect them to. Change the case or the conditions.';
}

/** The rule needs one case it applies to and one it does not. */
export function casesMissing(cases: CaseDraft[]): string[] {
  const out: string[] = [];
  if (!cases.some((c) => c.expected)) out.push('Test cases: add one the rule should apply to.');
  if (!cases.some((c) => !c.expected)) out.push('Test cases: add one the rule should not apply to.');
  return out;
}

export function casesToFixtures(cases: CaseDraft[]): Array<{ label?: string; classification: Record<string, unknown>; expected: boolean }> {
  return cases.map((c) => ({ ...(c.label.trim() ? { label: c.label.trim() } : {}), classification: caseClassification(c), expected: c.expected }));
}

/** The server refuses failing cases with `test_fixtures_json[i] ("label"): predicate evaluated to X, expected Y`; say it plainly. */
export function plainFixtureIssue(issue: string): string | null {
  const m = /^test_fixtures_json\[(\d+)\](?: \("(.*)"\))?: predicate evaluated to (true|false), expected (true|false)/.exec(issue);
  if (m) {
    const name = m[2] ? ` (${m[2]})` : '';
    return `Test case ${Number(m[1]) + 1}${name}: the rule ${m[3] === 'true' ? 'applies' : 'doesn’t apply'}, but the case expects it ${m[4] === 'true' ? 'to apply' : 'not to'}.`;
  }
  if (issue.startsWith('test_fixtures_json: at least one positive')) return 'Test cases: add one the rule should apply to.';
  if (issue.startsWith('test_fixtures_json: at least one negative')) return 'Test cases: add one the rule should not apply to.';
  if (issue.startsWith('test_fixtures')) return 'Test cases: check each case against the conditions.';
  return null;
}
